import React, { useEffect, useState } from "react";

import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import WarningIcon from "@mui/icons-material/Warning";
import { Box } from "@mui/material";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { getSourceDocumentLink } from "helpers/documentation/getDocumentationLinks";
import getSourceIcon from "helpers/Sources/getSourceIcon";

import EmptyState from "components/atoms/EmptyState";
import { ModalName } from "components/organisms/ModalRoot/ModalRoot.layout";

import PageContainer from "../../components/atoms/PageContainer";
import "../../css/spinner.css";
import { openModal } from "../../redux/slices/modal/modal.slice";
import { getAllSourcesRedux } from "../../redux/slices/sources/sources.asyncActions";
import {
    deleteLastAddedDest,
    deleteLastAddedSource,
} from "../../redux/slices/wizardSourceDestination/wizardSourceDestination.slice";
import {
    CardHeaderAction,
    CardHeaderLogo,
    CardHeaderTitle,
    StyledBottomCardContent,
    StyledCardContent,
    StyledCardHeader,
    StyledDestinationCard,
} from "../shared/CardStyles";
import { LogoIcon } from "../shared/CardStyles";
import DeleteSourceDialog from "../Trackers/TrackerDetails/SourceModal/components/DeleteSourceDialog";
import SourceModal from "../Trackers/TrackerDetails/SourceModal/SourceModal";
import "./source.css";

const Sources = () => {
    const language = useSelector((state) => state.languagesState.languages);
    const loading = useSelector(
        (state) => state.allSourcesState.getAllSources.status,
    );
    const location = useLocation();

    const handleShowSourceModal = () => {
        dispatch(
            openModal({
                modalName: ModalName.CREATE_SOURCE,
            }),
        );
    };

    const sources = useSelector(
        (state) => state.allSourcesState.getAllSources.sources,
    );
    const dispatch = useDispatch();
    useEffect(() => {
        return () => {
            dispatch(deleteLastAddedDest());
            dispatch(deleteLastAddedSource());
        };
    }, [dispatch]);
    useEffect(() => {
        dispatch(getAllSourcesRedux());
        location.search &&
            location.search.length > 0 &&
            handleShowSourceModal();
    }, [dispatch, location.search]);

    const handleGetSources = () => {
        dispatch(getAllSourcesRedux());
    };

    const SourceErrorTooltip = styled(Tooltip)(() => ({
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: "#000000",
            color: "#000",
            maxWidth: 220,
            fontSize: "12px",
            borderRadius: "10px",
            fontFamily: '"Nunito", serif',
            padding: "15px",
            boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
        },
        [`& .${tooltipClasses.tooltip} p`]: {
            fontWeight: "bold",
            fontFamily: '"Nunito", serif',
            color: "white",
            fontSize: "14px",
        },
        [`& .${tooltipClasses.tooltip} span`]: {
            fontWeight: "300",
            fontFamily: '"Nunito", serif',
            color: "white",
            fontSize: "12px",
        },
    }));

    const [sourceModalOpen, setSourceModalOpen] = useState(false);
    const [sourceModalItem, setSourceModalItem] = useState({});
    const [sourceModalInitialTab, setSourceModalInitialTab] = useState(null);

    const handleOpenSourceModal = (item, initialTab = null) => {
        setSourceModalOpen(true);
        setSourceModalItem(item);
        setSourceModalInitialTab(initialTab);
    };

    const sourceModalOnHide = () => {
        setSourceModalOpen(false);
        setSourceModalItem({});
        setSourceModalInitialTab(null);
    };

    // Always read the latest source from Redux so the modal reflects reconnect changes.
    const activeSourceModalItem =
        sources?.find((x) => x.id === sourceModalItem?.id) || sourceModalItem;

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteDialogSource, setDeleteDialogSource] = useState(null);

    const openDeleteSourceDialog = (item, event) => {
        event?.stopPropagation?.();
        setDeleteDialogSource(item);
        setDeleteDialogOpen(true);
    };

    const closeDeleteSourceDialog = () => {
        setDeleteDialogOpen(false);
        setDeleteDialogSource(null);
    };

    const openDocumentNewTab = (event, url) => {
        event.preventDefault();
        event.stopPropagation();
        openInNewTab(url);
        return false;
    };

    const openInNewTab = (url) => {
        window.open(url, "_blank", "noreferrer");
    };

    return (
        <PageContainer
            title={language?.sidebar?.sources ?? "Sources"}
            subtitle={
                language?.sources?.sources_desc ??
                "On this page, you can view sources, and create a new one by using the button."
            }
            additionalButtons={
                <Button
                    data-cy="sources-add"
                    onClick={handleShowSourceModal}
                    color="primary"
                    variant="contained"
                >
                    {language?.sources?.add_source ?? "Add Source"}
                </Button>
            }
            loading={loading === "idle" || loading === "loading"}
            variant="list"
        >
            {!sources?.length ? (
                <EmptyState
                    icon={<PublicOutlinedIcon />}
                    title={
                        language?.sources?.no_sources_yet ?? "No sources yet"
                    }
                    description={
                        language?.sources?.no_sources_yet_desc ??
                        "Add your first source to start collecting data for your trackers."
                    }
                />
            ) : (
                <Grid
                    container
                    rowSpacing={3}
                    columnSpacing={{ xs: 1, sm: 2, md: 3 }}
                >
                    {sources?.map((item, index) => (
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 3,
                            }}
                        >
                            <StyledDestinationCard
                                data-cy="source-card"
                                data-source-id={item.id}
                                onClick={() => handleOpenSourceModal(item)}
                                isError={item.status === 0}
                                className={`dashboard-tree-source-item no-effect dashboard-tree-source-item-${index} ${
                                    item.status === 0 ? "source-failed" : ""
                                }`}
                            >
                                <StyledCardHeader>
                                    <CardHeaderLogo>
                                        <LogoIcon
                                            src={getSourceIcon(
                                                sources,
                                                item?.source_type?.id,
                                            )}
                                        />
                                    </CardHeaderLogo>
                                    <CardHeaderTitle
                                        sx={{
                                            fontSize: 18,
                                            fontWeight: "700",
                                            fontFamily: "Nunito, sans-serif",
                                            color:
                                                item?.status === 0
                                                    ? "red"
                                                    : "black",
                                        }}
                                    >
                                        {item?.source_type?.name}
                                        {item?.status === 0 && (
                                            <SourceErrorTooltip
                                                placement="top"
                                                style={{ marginLeft: "10px" }}
                                                title={
                                                    <React.Fragment>
                                                        <Typography color="white">
                                                            {language
                                                                ?.tracker_detail
                                                                ?.error ??
                                                                "Error"}
                                                        </Typography>
                                                        <span>
                                                            {language
                                                                ?.tracker_detail
                                                                ?.source_error_tooltip_message ??
                                                                "Since the Access Token is expired, you must reconnect the source."}
                                                        </span>
                                                    </React.Fragment>
                                                }
                                            >
                                                <WarningIcon></WarningIcon>
                                            </SourceErrorTooltip>
                                        )}
                                    </CardHeaderTitle>
                                    {[
                                        "capi",
                                        "js",
                                        "androidcapi",
                                        "ioscapi",
                                        "redirect",
                                    ].includes(item?.source_type?.code) ===
                                        false &&
                                        getSourceDocumentLink(
                                            item?.source_type?.code,
                                        ) !== "" && (
                                            <CardHeaderAction>
                                                <a
                                                    data-cy="source-documentation"
                                                    href="javascript:;"
                                                    onClick={(e) =>
                                                        openDocumentNewTab(
                                                            e,
                                                            getSourceDocumentLink(
                                                                item
                                                                    ?.source_type
                                                                    ?.code,
                                                            ),
                                                        )
                                                    }
                                                    className="document-view-link width-auto"
                                                    style={{ color: "#ed6c02" }}
                                                >
                                                    <MenuBookOutlinedIcon />
                                                </a>
                                            </CardHeaderAction>
                                        )}
                                </StyledCardHeader>
                                <StyledCardContent>
                                    {item?.allowed_domains !== undefined &&
                                    item?.allowed_domains.length > 0 ? (
                                        <>
                                            <span
                                                style={{
                                                    display: "block",
                                                    fontSize: "14px",
                                                    color: "black",
                                                    fontWeight: "500",
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        display: "block",
                                                        fontSize: "12px",
                                                        color: "#ABABAB",
                                                        fontWeight: "400",
                                                    }}
                                                >
                                                    {language?.tracker_detail
                                                        ?.domain ?? "Domain"}
                                                </span>
                                                {
                                                    item?.allowed_domains[0]
                                                        ?.domain
                                                }
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <span
                                                style={{
                                                    display: "block",
                                                    fontSize: "14px",
                                                    color: "black",
                                                    fontWeight: "500",
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        display: "block",
                                                        fontSize: "12px",
                                                        color: "#ABABAB",
                                                        fontWeight: "400",
                                                    }}
                                                >
                                                    {language?.tracker_detail
                                                        ?.name ?? "Name"}
                                                </span>
                                                <div
                                                    style={{
                                                        wordBreak: "break-word",
                                                        lineClamp: 2,
                                                        display: "-webkit-box",
                                                        "-webkit-line-clamp": 2,
                                                        "-webkit-box-orient":
                                                            "vertical",
                                                        overflow: "hidden",
                                                        textOverflow:
                                                            "ellipsis",
                                                    }}
                                                >
                                                    {item?.name}
                                                </div>
                                            </span>
                                        </>
                                    )}
                                </StyledCardContent>
                                {item?.trackers && item?.trackers.length > 0 ? (
                                    <StyledBottomCardContent>
                                        <div className="in-use-area">
                                            {language?.destinations?.in_use ??
                                                "In Use"}
                                        </div>
                                    </StyledBottomCardContent>
                                ) : (
                                    <StyledBottomCardContent>
                                        <div className="not-in-use-area">
                                            {language?.destinations
                                                ?.not_in_use ?? "Not In Use"}
                                        </div>
                                    </StyledBottomCardContent>
                                )}
                                {item?.status === 0 && (
                                    <StyledBottomCardContent>
                                        <Button
                                            data-cy="source-reconnect"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenSourceModal(
                                                    item,
                                                    "reconnect",
                                                );
                                            }}
                                            style={{
                                                minWidth: "unset !important",
                                            }}
                                            color="primary"
                                            variant="contained"
                                        >
                                            {language?.tracker_detail
                                                ?.reconnect ?? "Reconnect"}
                                        </Button>
                                        <Tooltip
                                            title={
                                                item?.trackers?.length > 0
                                                    ? (language?.destinations
                                                          ?.cannot_delete_in_use ??
                                                      "This destination is in use by a tracker and cannot be deleted.")
                                                    : ""
                                            }
                                            placement="top"
                                        >
                                            <Box>
                                                <Button
                                                    data-cy="source-remove"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openDeleteSourceDialog(
                                                            item,
                                                            e,
                                                        );
                                                    }}
                                                    color="error"
                                                    variant="outlined"
                                                    disabled={
                                                        item?.trackers?.length >
                                                        0
                                                    }
                                                    style={{
                                                        minWidth:
                                                            "unset !important",
                                                    }}
                                                >
                                                    {language?.tracker_detail
                                                        ?.remove ?? "Remove"}
                                                </Button>
                                            </Box>
                                        </Tooltip>
                                    </StyledBottomCardContent>
                                )}
                            </StyledDestinationCard>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* source detail modal */}
            <SourceModal
                isOpen={sourceModalOpen}
                source={activeSourceModalItem}
                language={language}
                onClose={sourceModalOnHide}
                allowedDomains={activeSourceModalItem?.allowed_domains || []}
                mode="standalone"
                onRefresh={handleGetSources}
                initialTab={sourceModalInitialTab}
            />

            {deleteDialogSource?.id != null && (
                <DeleteSourceDialog
                    open={deleteDialogOpen}
                    onClose={closeDeleteSourceDialog}
                    source={deleteDialogSource}
                    language={language}
                    mode="standalone"
                    onRefresh={handleGetSources}
                />
            )}
        </PageContainer>
    );
};

export default Sources;
